/**
 * Login Page
 * Handles user authentication
 * - Dev mode: Allows testing with different internal users + external user forms
 * - Production: Azure AD (internal) or embedded Keycloak login/register (external)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, extractApiData } from '../utils/apiClient';
import logger from '../utils/logger';

const LoginPage = ({ onLogin, onKeycloakLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [devEmail, setDevEmail] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loginTab, setLoginTab] = useState(0); // 0 = Internal, 1 = Client Portal
  const [externalMode, setExternalMode] = useState('login'); // 'login' or 'register'

  // External login fields
  const [extEmail, setExtEmail] = useState('');
  const [extPassword, setExtPassword] = useState('');

  // External registration fields
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regCompanyName, setRegCompanyName] = useState('');
  const [regPhone, setRegPhone] = useState('');

  // Dev mode toggle - can be overridden by user
  const isDevEnv = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
  const [devModeEnabled, setDevModeEnabled] = useState(isDevEnv);
  const isDev = devModeEnabled;

  // Extract tenant code from URL subdomain or query parameter
  // In production: africanalliance.capitalmanagement.com -> "africanalliance"
  // In dev: ?tenant=african_alliance -> "african_alliance"
  const getTenantCode = () => {
    // 1. Check query parameter (useful for dev/testing)
    const tenantParam = searchParams.get('tenant');
    if (tenantParam) return tenantParam;

    // 2. Extract from subdomain (production Azure deployment)
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const subdomain = parts[0].toLowerCase();
      // Ignore common non-tenant subdomains
      if (!['www', 'api', 'app', 'admin'].includes(subdomain)) {
        return subdomain;
      }
    }

    return null;
  };

  const tenantCode = getTenantCode();

  // Check for error from redirect
  useEffect(() => {
    const authError = searchParams.get('error');
    if (authError === 'pending_approval') {
      navigate('/pending-approval');
    } else if (authError === 'not_authorized') {
      setError('Your account is not authorized. Please contact your administrator.');
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (isDev) {
      loadAvailableUsers();
    }
  }, [isDev]);

  const loadAvailableUsers = async () => {
    try {
      const response = await apiGet('/admin/authorized-users');
      setAvailableUsers(extractApiData(response));
      const defaultEmail = process.env.REACT_APP_DEV_USER_EMAIL;
      if (defaultEmail) {
        setDevEmail(defaultEmail);
      }
    } catch (err) {
      // Silently fail if we can't load users (e.g., not authenticated yet)
      // This is expected when the endpoint requires authentication
      logger.debug('Could not load user list (not authenticated)');
      const defaultEmail = process.env.REACT_APP_DEV_USER_EMAIL;
      if (defaultEmail) {
        setDevEmail(defaultEmail);
      }
    }
  };

  const handleDevLogin = async () => {
    if (!devEmail) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (onLogin) {
        const success = await onLogin(devEmail);
        if (success) {
          navigate('/');
        } else {
          setError('User not found or not authorized. Please check the email address.');
        }
      }
    } catch (err) {
      setError('Failed to verify user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLogin = () => {
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=/';
  };

  const handleExternalLogin = async () => {
    if (!extEmail || !extPassword) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiPost('/auth/login', {
        email: extEmail,
        password: extPassword,
      });

      if (response.success) {
        // Store tokens
        sessionStorage.setItem('keycloakToken', response.accessToken);
        sessionStorage.setItem('keycloakRefreshToken', response.refreshToken);
        sessionStorage.setItem('userType', 'external_client');
        sessionStorage.setItem('externalUser', JSON.stringify(response.user));

        if (onKeycloakLogin) {
          const success = await onKeycloakLogin(response.user);
          if (success) {
            // Small delay to ensure React state updates are processed
            setTimeout(() => {
              navigate('/client-portal/dashboard');
            }, 100);
          }
        }
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.pendingApproval) {
        navigate('/pending-approval');
        return;
      }
      setError(data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleExternalRegister = async () => {
    setError(null);

    if (!regEmail || !regPassword || !regFirstName || !regLastName) {
      setError('Please fill in all required fields');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await apiPost('/auth/register', {
        email: regEmail,
        password: regPassword,
        firstName: regFirstName,
        lastName: regLastName,
        companyName: regCompanyName,
        phone: regPhone,
        tenantCode: tenantCode,
      });

      if (response.success) {
        setSuccess(response.message || 'Registration successful. Your account is pending approval.');
        // Clear form
        setRegEmail('');
        setRegPassword('');
        setRegConfirmPassword('');
        setRegFirstName('');
        setRegLastName('');
        setRegCompanyName('');
        setRegPhone('');
        // Switch to login after short delay
        setTimeout(() => setExternalMode('login'), 3000);
      }
    } catch (err) {
      const data = err.response?.data;
      setError(data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderExternalLogin = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Sign in to your investment portal
      </Typography>

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={extEmail}
        onChange={(e) => setExtEmail(e.target.value)}
        placeholder="your@email.com"
        sx={{ mb: 2 }}
        disabled={loading}
        onKeyDown={(e) => e.key === 'Enter' && handleExternalLogin()}
      />

      <TextField
        fullWidth
        label="Password"
        type="password"
        value={extPassword}
        onChange={(e) => setExtPassword(e.target.value)}
        sx={{ mb: 3 }}
        disabled={loading}
        onKeyDown={(e) => e.key === 'Enter' && handleExternalLogin()}
      />

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleExternalLogin}
        disabled={loading || !extEmail || !extPassword}
        sx={{ py: 1.5, mb: 2, backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => { setExternalMode('register'); setError(null); setSuccess(null); }}
          >
            Register here
          </Link>
        </Typography>
      </Box>
    </>
  );

  const renderExternalRegister = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Create your investor portal account
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="First Name *"
          value={regFirstName}
          onChange={(e) => setRegFirstName(e.target.value)}
          disabled={loading}
        />
        <TextField
          fullWidth
          label="Last Name *"
          value={regLastName}
          onChange={(e) => setRegLastName(e.target.value)}
          disabled={loading}
        />
      </Box>

      <TextField
        fullWidth
        label="Email Address *"
        type="email"
        value={regEmail}
        onChange={(e) => setRegEmail(e.target.value)}
        placeholder="your@email.com"
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Company Name"
        value={regCompanyName}
        onChange={(e) => setRegCompanyName(e.target.value)}
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Phone Number"
        value={regPhone}
        onChange={(e) => setRegPhone(e.target.value)}
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Password *"
        type="password"
        value={regPassword}
        onChange={(e) => setRegPassword(e.target.value)}
        helperText="Minimum 8 characters"
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Confirm Password *"
        type="password"
        value={regConfirmPassword}
        onChange={(e) => setRegConfirmPassword(e.target.value)}
        error={regConfirmPassword.length > 0 && regPassword !== regConfirmPassword}
        helperText={regConfirmPassword.length > 0 && regPassword !== regConfirmPassword ? 'Passwords do not match' : ''}
        sx={{ mb: 3 }}
        disabled={loading}
      />

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleExternalRegister}
        disabled={loading || !regEmail || !regPassword || !regFirstName || !regLastName}
        sx={{ py: 1.5, mb: 2, backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Register'}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
        Registration requires administrator approval
      </Typography>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => { setExternalMode('login'); setError(null); setSuccess(null); }}
          >
            Sign in
          </Link>
        </Typography>
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Cpath d='M0 400 Q150 320 300 420 T600 360 T900 440 T1200 380' fill='none' stroke='rgba(148,163,184,0.18)' stroke-width='1.5'/%3E%3Cpath d='M0 430 Q180 350 360 460 T720 390 T1080 470 T1200 420' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 370 Q120 300 240 380 T480 340 T720 400 T960 350 T1200 370' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 460 Q200 390 400 490 T800 420 T1200 470' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 340 Q100 280 200 350 T400 310 T600 360 T800 320 T1000 370 T1200 340' fill='none' stroke='rgba(148,163,184,0.10)' stroke-width='1'/%3E%3Cpath d='M0 490 Q250 440 500 520 T1000 480 T1200 500' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 310 Q300 260 600 330 T1200 300' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cpath d='M0 520 Q200 480 400 550 T800 510 T1200 540' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cellipse cx='150' cy='200' rx='350' ry='250' fill='rgba(59,130,246,0.08)' /%3E%3Cellipse cx='950' cy='600' rx='400' ry='300' fill='rgba(99,102,241,0.07)' /%3E%3Cellipse cx='600' cy='80' rx='250' ry='180' fill='rgba(148,163,184,0.06)' /%3E%3C/svg%3E")
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pointerEvents: 'none',
        },
      }}
    >
      <Card sx={{
        maxWidth: 460,
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 20px 60px rgba(0,0,0,0.3)',
        position: 'relative',
        zIndex: 1,
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo-full.svg"
              alt="Capital Management"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 100,
              }}
            />
          </Box>

          {/* Dev Mode Toggle - Only show if dev env is enabled */}
          {isDevEnv && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="text"
                color={devModeEnabled ? 'warning' : 'inherit'}
                onClick={() => {
                  setDevModeEnabled(!devModeEnabled);
                  setError(null);
                  setSuccess(null);
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {devModeEnabled ? '🔧 Switch to Production Mode' : '🔧 Switch to Dev Mode'}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {isDev ? (
            // Development login form
            (<>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Development Mode - Enter user email to login
              </Typography>
              <TextField
                fullWidth
                label="Email Address"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder="user@company.com"
                sx={{ mb: 2 }}
                disabled={loading}
              />
              {availableUsers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Available users:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                    {availableUsers.slice(0, 5).map(user => (
                      <Button
                        key={user.id}
                        size="small"
                        variant="outlined"
                        onClick={() => setDevEmail(user.email)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        {user.email.split('@')[0]}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleDevLogin}
                disabled={loading || !devEmail}
                sx={{ mt: 2, backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In (Dev Mode)'}
              </Button>
              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  EXTERNAL USERS
                </Typography>
              </Divider>
              {/* Embedded external login/register forms in dev mode too */}
              {externalMode === 'login' ? renderExternalLogin() : renderExternalRegister()}
            </>)
          ) : (
            // Production login with tabs for Internal vs Client Portal
            (<>
              <Tabs
                value={loginTab}
                onChange={(e, newValue) => { setLoginTab(newValue); setError(null); setSuccess(null); }}
                variant="fullWidth"
                sx={{ mb: 3, '& .MuiTab-root.Mui-selected': { color: '#1e293b' }, '& .MuiTabs-indicator': { backgroundColor: '#1e293b' } }}
              >
                <Tab
                  icon={<BusinessIcon />}
                  label="Internal Staff"
                  iconPosition="start"
                />
                <Tab
                  icon={<PersonIcon />}
                  label="Client Portal"
                  iconPosition="start"
                />
              </Tabs>
              {loginTab === 0 ? (
                // Internal Staff - Azure AD
                (<>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Sign in with your corporate Microsoft account
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleAzureLogin}
                    disabled={loading}
                    sx={{ py: 1.5, backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Sign in with Microsoft'}
                  </Button>
                </>)
              ) : (
                // Client Portal - Embedded login/register
                (externalMode === 'login' ? renderExternalLogin() : renderExternalRegister())
              )}
            </>)
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 4 }}
          >
            By signing in, you agree to the terms of use.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
